import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { 
  faSort, 
  faSortDown, 
  faSortUp,
  faAngleDoubleLeft,
  faAngleLeft,
  faAngleRight,
  faAngleDoubleRight,
  faExternalLinkAlt,
  faWarning,
  faPaperPlane,
  faTimes,
  faCopy,
  faCheck,
  faInfoCircle,
  faTrash,
  faLock
} from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import { IContractDetail } from 'src/app/models/contract-detail.model';
import { IResendDocumentsResponse } from 'src/app/models/document.model';
import { IError } from 'src/app/models/error.model';
import { DocumentsService } from 'src/app/services/documents.service';
import { ModulesService } from 'src/app/services/modules.service';
import { SearchService } from '../../services/search.service';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';

@Component({
  selector: 'app-pending-signatures-v2',
  templateUrl: './pending-signatures.component.html',
  styleUrls: ['./pending-signatures.component.sass']
})
export class PendingSignaturesV2Component implements OnInit, OnDestroy {
  Math = Math;
  loading = false;
  pageSize = 10;
  currentPage = 0;
  totalPages = 1;
  totalElements = 0;
  
  contracts: IContractDetail[] = [];
  filterType: 'COMPLETED' | 'NOT_COMPLETED' | 'ALL' | 'PENDING_PATIENT_SIGNATURE' = 'NOT_COMPLETED';
  initialDate = '';
  finalDate = '';

  private searchSubscription?: Subscription;

  // Resend Modal State
  showResendModal = false;
  resendLoading: string | null = null;
  resendModalLoading = false;
  resendDocumentsData: IResendDocumentsResponse | null = null;
  resendError: string | null = null;



  // Delete Modal State
  showConfirmDeletePopup = false;
  pendingDeleteAnswerGroupId: string | null = null;
  confirmDeleteLoading = false;

  // Lock tooltip state
  lockTooltipVisibleFor: string | null = null;
  lockTooltipLeft = 0;
  lockTooltipTop = 0;

  // Status mapping (base – SIGNED_ONLY_BY_RESPONSIBLE is resolved dynamically via getContractStatus)
  private baseStatusMapping: { [key: string]: { message: string; color: string } } = {
    'PENDING': {
      message: 'Não ass.',
      color: 'text-red-500'
    },
    'SIGNED_ONLY_BY_CLIENT': {
      message: 'Pend. Res.',
      color: 'text-blue-500'
    },
    'SIGNED': {
      message: 'Assinado',
      color: 'text-emerald-500'
    }
  };

  // Icons
  faSort = faSort;
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  faAngleDoubleLeft = faAngleDoubleLeft;
  faAngleLeft = faAngleLeft;
  faAngleRight = faAngleRight;
  faAngleDoubleRight = faAngleDoubleRight;
  faExternalLinkAlt = faExternalLinkAlt;
  faWarning = faWarning;
  faPaperPlane = faPaperPlane;
  faTimes = faTimes;
  faCopy = faCopy;
  faCheck = faCheck;
  faInfoCircle = faInfoCircle;
  faTrash = faTrash;
  faLock = faLock;

  pageSizeOptions = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' }
  ];

  viewModules$: Observable<{ moduleName: string; moduleNamePascalCase: string; moduleNameInSingular: string; }>;

  constructor(
    private documentsService: DocumentsService,
    private router: Router,
    private route: ActivatedRoute,
    private searchService: SearchService,
    public modulesService: ModulesService,
    private store: Store<{ warningPopup: IError }>
  ) {
    this.viewModules$ = combineLatest({
      moduleName: this.modulesService.moduleName$,
      moduleNamePascalCase: this.modulesService.moduleNamePascalCase$,
      moduleNameInSingular: this.modulesService.moduleNameInSingular$
    });
  }

  ngOnInit(): void {
    this.loading = true;
    this.searchService.setSearchTerm('');
    
    // Verifica se veio do alerta de contratos pendentes
    const fromWarning = this.route.snapshot.queryParams['fromWarning'];
    if (fromWarning === 'true') {
      this.filterType = 'PENDING_PATIENT_SIGNATURE';
    }
    
    this.getContracts(0, this.pageSize);

    this.searchSubscription = this.searchService.searchTerm$.subscribe(() => {
      this.getContracts(0, this.pageSize);
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  getContracts(page: number, size: number): void {
    this.loading = true;
    
    this.documentsService.getDocumentsWithFilters(
      '', // patientId vazio para listar todos
      page,
      size,
      this.filterType,
      this.initialDate,
      this.finalDate,
      this.modulesService.moduleId || ''
    ).then((response) => {
      this.contracts = response.data;
      this.currentPage = response.current_page;
      this.totalPages = response.total_pages;
      this.totalElements = response.total_elements;
      this.loading = false;
    }).catch((error) => {
      console.error('Erro ao buscar contratos:', error);
      this.loading = false;
    });
  }

  getContractStatus(status: string): { message: string; color: string } {
    if (status === 'SIGNED_ONLY_BY_RESPONSIBLE') {
      const singular = this.modulesService.moduleNameInSingular || 'Pac.';
      const shortSingular = singular.length > 6 ? singular.slice(0, 6) + '.' : singular;
      return { message: `Pend. ${shortSingular}`, color: 'text-red-500' };
    }
    return this.baseStatusMapping[status] || { message: status, color: 'text-gray-500' };
  }

  hasAnyPendingDocument(document: IContractDetail): boolean {
    if (!document.autentique_info_list) return false;
    return document.autentique_info_list.some(contract => 
      ['PENDING', 'SIGNED_ONLY_BY_RESPONSIBLE'].includes(contract.status)
    );
  }

  /**
   * Verifica se o documento (grupo de respostas) pode ser excluído.
   * Regras implementadas:
   * - Deve permitir exclusão quando o documento estiver pendente / aguardando o paciente/responsável;
   * - Deve bloquear exclusão quando o paciente já assinou (SIGNED_ONLY_BY_CLIENT) ou quando ambos assinaram (SIGNED).
   */
  canDeleteDocument(document: IContractDetail): boolean {
    if (!document.autentique_info_list || document.autentique_info_list.length === 0) return false;
    // Se existir qualquer contrato onde o paciente já assinou (SIGNED_ONLY_BY_CLIENT) ou ambos assinaram (SIGNED), bloqueia exclusão
    const disallowedStatuses = ['SIGNED', 'SIGNED_ONLY_BY_CLIENT'];
    return !document.autentique_info_list.some(contract => disallowedStatuses.includes(contract.status));
  }

  // ========== Lock Tooltip ==========

  showLockTooltip(event: MouseEvent, answerGroupId: string): void {
    this.lockTooltipVisibleFor = answerGroupId;
    this.lockTooltipLeft = event.clientX;
    this.lockTooltipTop = event.clientY;
  }

  hideLockTooltip(): void {
    this.lockTooltipVisibleFor = null;
  }

  // ========== Delete Document Feature ==========

  deleteDocument(answerGroupId: string): void {
    if (!answerGroupId) return;
    this.showDeleteModal(answerGroupId);
  }

  showDeleteModal(answerGroupId?: string): void {
    if (answerGroupId) this.pendingDeleteAnswerGroupId = answerGroupId;
    this.showConfirmDeletePopup = true;
  }

  closeDeleteModal(): void {
    this.showConfirmDeletePopup = false;
    this.pendingDeleteAnswerGroupId = null;
  }

  confirmDelete(): void {
    const id = this.pendingDeleteAnswerGroupId;
    if (!id) return;
    this.confirmDeleteLoading = true;
    this.documentsService.deleteAnswerGroup(id)
      .then((response: any) => {
        const successMessage = response?.message || 'Documento excluído com sucesso.';
        this.store.dispatch(setWarningText(successMessage, 200));
        this.closeDeleteModal();
        this.getContracts(this.currentPage, this.pageSize);
      })
      .catch((error: any) => {
        console.error('Erro ao excluir documento:', error);
        // Mapear códigos de erro do backend para mensagens amigáveis
        const errorCode = error?.error_code || error?.code || '';
        let errMsg = error?.message || 'Erro ao excluir documento. Tente novamente.';
        
        if (errorCode === 'document_not_found' || errMsg.toLowerCase().includes('not_found')) {
          errMsg = 'Documento não encontrado. Ele pode já ter sido excluído.';
        } else if (errorCode === 'document_already_signed' || errMsg.toLowerCase().includes('signed')) {
          errMsg = 'Não é possível excluir um documento que já foi assinado.';
        } else if (errorCode === 'unauthorized' || error?.status === 401 || error?.status === 403) {
          errMsg = 'Você não tem permissão para excluir este documento.';
        }
        
        const errStatus = error?.status || 500;
        this.store.dispatch(setWarningText(errMsg, errStatus));
        this.closeDeleteModal();
        this.getContracts(this.currentPage, this.pageSize); // Atualiza a lista para refletir o estado atual
      })
      .finally(() => {
        this.confirmDeleteLoading = false;
      });
  }

  openContract(contractId: string, status: string): void {
    if (status === 'Assinado') {
      window.open(`https://secret.autentique.com.br/documentos/${contractId}/assinado.pdf`, '_blank');
    } else {
      window.open(`https://painel.autentique.com.br/documentos/${contractId}`, '_blank');
    }
  }

  changeFilter(filter: 'COMPLETED' | 'NOT_COMPLETED' | 'ALL' | 'PENDING_PATIENT_SIGNATURE'): void {
    this.filterType = filter;
    this.getContracts(0, this.pageSize);
  }

  onDateChange(): void {
    this.getContracts(0, this.pageSize);
  }

  clearDateFilters(): void {
    this.initialDate = '';
    this.finalDate = '';
    this.getContracts(0, this.pageSize);
  }

  // ========== Resend Documents Feature ==========

  async openResendModal(answerGroupId: string): Promise<void> {
    this.resendLoading = answerGroupId;
    this.showResendModal = true;
    this.resendModalLoading = true;
    this.resendDocumentsData = null;
    this.resendError = null;

    try {
      this.resendDocumentsData = await this.documentsService.getPendingDocumentUrls(answerGroupId);
    } catch (error: unknown) {
      const err = error as { message?: string };
      this.resendError = err?.message || 'Erro ao buscar links de assinatura. Tente novamente.';
    } finally {
      this.resendModalLoading = false;
      this.resendLoading = null;
    }
  }

  closeResendModal(): void {
    this.showResendModal = false;
    this.resendDocumentsData = null;
    this.resendError = null;
  }

  copyLink(url: string): void {
    this.copyToClipboard(url, 'Link copiado!');
  }

  copyWhatsAppMessage(): void {
    const message = this.buildWhatsAppMessage();
    this.copyToClipboard(message, 'Mensagem copiada!');
  }

  buildWhatsAppMessage(): string {
    if (!this.resendDocumentsData) return '';

    let message = 'Olá! Seguem os links para assinatura dos documentos:\n\n';
    
    this.resendDocumentsData.documents.forEach((doc, index) => {
      message += `${index + 1}. ${doc.document_name}\n${doc.signature_url}\n\n`;
    });

    return message;
  }

  private copyToClipboard(text: string, successMessage: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.store.dispatch(setWarningText(successMessage, 200));
    }).catch(() => {
      this.store.dispatch(setWarningText('Erro ao copiar'));
    });
  }

  // Paginação
  changePageSize(event: Event): void {
    this.pageSize = +(<HTMLSelectElement>event.target).value;
    this.getContracts(0, this.pageSize);
  }

  goToFirstPage(): void {
    if (this.currentPage > 0) {
      this.getContracts(0, this.pageSize);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.getContracts(this.currentPage - 1, this.pageSize);
    }
  }

  goToNextPage(): void {
    if (this.currentPage + 1 < this.totalPages) {
      this.getContracts(this.currentPage + 1, this.pageSize);
    }
  }

  goToLastPage(): void {
    if (this.currentPage + 1 < this.totalPages) {
      this.getContracts(this.totalPages - 1, this.pageSize);
    }
  }

  generatePagination(current: number, total: number): (string | number)[] {
    const range = (start: number, end: number): number[] => {
      return Array.from({ length: (end - start) }, (v, k) => k + start);
    };
    let pages: (string | number)[] = [1];
    let left = current - 1;
    let right = current + 1;
    if (left < 2) right += 2 - left;
    if (right > total - 1) left -= right - (total - 1);
    left = Math.max(2, left);
    right = Math.min(total - 1, right);
    if (left > 2) pages.push('...');
    pages = pages.concat(range(left, right + 1));
    if (right < total - 1) pages.push('...');
    if (total > 1 && total !== pages[pages.length - 1]) pages.push(total);
    return pages;
  }

  navigateTo(page: number | string): void {
    if (page !== '...') {
      this.getContracts(+page - 1, this.pageSize);
    }
  }

  // Exibe apenas o primeiro nome e o primeiro sobrenome (ex: "Igor Bitencourt Parente" => "Igor Bitencourt").
  // Se houver apenas um nome, retorna ele inteiro; se for nulo, retorna '-'.
  formatProfessionalShort(fullName?: string | null): string {
    if (!fullName) return '-';
    const parts = fullName.trim().split(/\s+/).filter(p => p.length > 0);
    if (parts.length === 0) return '-';
    if (parts.length === 1) return parts[0];
    const firstName = parts[0];
    const firstSurname = parts[1];
    return `${firstName} ${firstSurname}`;
  }

  // Trunca o nome do contrato para o tamanho informado (default 20) e adiciona reticências se necessário
  truncateContractName(name?: string | null, maxLength: number = 20): string {
    if (!name) return '-';
    const trimmed = name.trim();
    if (trimmed.length <= maxLength) return trimmed;
    return trimmed.substring(0, maxLength) + '...';
  }
}
